import { editor, languages, Position, Range } from 'monaco-editor/esm/vs/editor/editor.api';

import { granetSyntaxMeta } from './syntax';

type CompletionItem = Omit<languages.CompletionItem, 'range'> & {
    // typings error, field should be optional
    range?: languages.SelectionRange['range'];
};

const makeTypeItem = (str: string): CompletionItem => ({
    label: str,
    insertText: str,
    kind: languages.CompletionItemKind.Variable,
});

const makeNonTerminalItem = (str: string, model: editor.ITextModel, position: Position): CompletionItem => ({
    label: str,
    insertText: str,
    kind: languages.CompletionItemKind.Variable,
    range: new Range(
        position.lineNumber,
        model.getLineContent(position.lineNumber).lastIndexOf('$') + 1 || position.column,
        position.lineNumber,
        position.column,
    ),
});

const makeDirectiveItem = (str: string, model: editor.ITextModel, position: Position): CompletionItem => ({
    label: str,
    insertText: str,
    kind: languages.CompletionItemKind.Constant,
    range: new Range(
        position.lineNumber,
        model.getLineContent(position.lineNumber).lastIndexOf('%') + 1 || position.column,
        position.lineNumber,
        position.column,
    ),
});

const makeKeywordItem = (kw: string): CompletionItem => ({
    label: kw,
    insertText: `${kw}:\n\t`,
    kind: languages.CompletionItemKind.Keyword,
    insertTextRules: languages.CompletionItemInsertTextRule.InsertAsSnippet,
});

const getNonTerminals = (text: string) => {
    const matches = text.match(/^\$[\w.]+/gm) ?? [];

    return matches;
};

// $ - to match non-terminals, % - to match directives
const nonAlpha = /[^а-яА-Яё\w$\%]/;

const parseModel = (model: editor.ITextModel, position: Position) => {
    const line = model.getLineContent(position.lineNumber);
    const nonTerminals = getNonTerminals(model.getValue());

    const lineBefore = line.substr(0, position.column - 1);

    const isSlotType = Boolean(lineBefore.match(/\s+type:\s*/));
    const isTopLevelWord = position.column === 1 || Boolean(lineBefore.match(/^\w+$/));
    const isTopLevelNonTerminal = position.column === 1 || Boolean(lineBefore.match(/^\$\w*$/));

    const lineWords = lineBefore.split(nonAlpha);
    const currentWord = lineWords[lineWords.length - 1];

    return { isSlotType, isTopLevelWord, nonTerminals, isTopLevelNonTerminal, currentWord };
};

export const getGranetCompletionProvider = (customEntities: string[]): languages.CompletionItemProvider => {
    return {
        provideCompletionItems: (model, position) => {
            const {
                isSlotType,
                isTopLevelWord,
                nonTerminals: parsedNonTerminals,
                isTopLevelNonTerminal,
                currentWord,
            } = parseModel(model, position);

            const entities = [...granetSyntaxMeta.builtinEntities, ...customEntities];

            if (isSlotType) {
                return {
                    suggestions: entities.map(makeTypeItem) as languages.CompletionItem[],
                };
            }

            const nonTerminals = [...parsedNonTerminals, ...entities.map((e) => `$${e}`)];

            const keywordsSuggestions: CompletionItem[] = isTopLevelWord
                ? granetSyntaxMeta.keywords.map(makeKeywordItem)
                : [];

            const nonTerminalsSuggestions: CompletionItem[] =
                !isTopLevelNonTerminal && currentWord.startsWith('$')
                    ? nonTerminals.map((nt) => makeNonTerminalItem(nt, model, position))
                    : [];

            const directivesSuggestions: CompletionItem[] = currentWord.startsWith('%')
                ? granetSyntaxMeta.directives.map((dir) => makeDirectiveItem(dir, model, position))
                : [];

            return {
                suggestions: [
                    ...keywordsSuggestions,
                    ...nonTerminalsSuggestions,
                    ...directivesSuggestions,
                ] as languages.CompletionItem[],
            };
        },
        triggerCharacters: ['$', '%'],
    };
};

export const granetEntitiesCompletionProvider: languages.CompletionItemProvider = {
    provideCompletionItems: (model, position) => {
        const entities = granetSyntaxMeta.builtinEntities;
        const {
            isTopLevelWord,
            nonTerminals: parsedNonTerminals,
            isTopLevelNonTerminal,
            currentWord,
        } = parseModel(model, position);

        const nonTerminals = [...parsedNonTerminals, ...entities.map((e) => `$${e}`)];

        const entityDeclSuggestion: CompletionItem[] = isTopLevelWord
            ? [
                  {
                      label: 'entity',
                      insertText: 'entity ${1:Name}:\n\tvalues:\n\t\t$0',
                      kind: languages.CompletionItemKind.Class,
                      insertTextRules: languages.CompletionItemInsertTextRule.InsertAsSnippet,
                  },
              ]
            : [];

        const nonTerminalsSuggestions: CompletionItem[] =
            !isTopLevelNonTerminal && currentWord.startsWith('$')
                ? nonTerminals.map((nt) => makeNonTerminalItem(nt, model, position))
                : [];

        const directivesSuggestions: CompletionItem[] = currentWord.startsWith('%')
            ? granetSyntaxMeta.directives.map((dir) => makeDirectiveItem(dir, model, position))
            : [];

        return {
            suggestions: [
                ...nonTerminalsSuggestions,
                ...directivesSuggestions,
                ...entityDeclSuggestion,
            ] as languages.CompletionItem[],
        };
    },
    triggerCharacters: ['$', '%'],
};
